import { Controller } from "@hotwired/stimulus"

// A sidebar group, enhanced. The group works with this file deleted: <details>
// opens and closes on its own, and the server sends the group holding the
// current page open on every request, so the page you are on is never hidden.
//
// Two jobs, both of them small.
//
// First, aria-expanded on the summary. A native summary already announces its
// state, and the attribute is written server side to match the initial `open`.
// The moment someone toggles the group, that static attribute would be a lie,
// so it is kept in step here.
//
// Second, a group you shut by hand stays shut when you navigate. The server
// rule alone would reopen a group every time you landed on a page it does not
// contain, which reads as the sidebar undoing your decision. The choice is
// remembered per group label in localStorage, and the current group is never
// reopened as shut: a group holding the page you are on stays open whatever
// you last chose, because a collapsed group hiding the current page is a
// sidebar that has lost its place.
export default class extends Controller {
  static values = { key: String }

  connect() {
    this.sync = this.sync.bind(this)
    this.element.addEventListener("toggle", this.sync)
    this.restore()
    this.sync()
  }

  disconnect() {
    this.element.removeEventListener("toggle", this.sync)
  }

  restore() {
    // The current group is server-open and stays that way.
    if (this.element.querySelector("[aria-current='page']")) return

    const remembered = this.read()
    if (remembered !== null) this.element.open = remembered
  }

  sync() {
    const summary = this.element.querySelector("summary")
    if (summary) summary.setAttribute("aria-expanded", this.element.open ? "true" : "false")
    this.write(this.element.open)
  }

  storageKey() {
    return `panel-nav-group:${this.keyValue}`
  }

  // Private browsing and blocked storage both throw on access rather than
  // returning nothing, and a navigation sidebar is not worth an exception.
  read() {
    try {
      const value = window.localStorage.getItem(this.storageKey())
      return value === null ? null : value === "open"
    } catch (error) {
      return null
    }
  }

  write(open) {
    try {
      window.localStorage.setItem(this.storageKey(), open ? "open" : "shut")
    } catch (error) {
      // Nothing to do. The group still works, it just forgets.
    }
  }
}
